/**
 * Used for not getting the keys wrong when trying to use getUserCooldownRemaining
 */
export const FORTUNE_COOLDOWN_KEY = {
  // ACTIVE: 'fortuneCookie_active',
  COOLDOWN: 'fortuneCookie_cooldown',
};

/**
 * Manages the stuff in the savestate that is related to the fortune cookie!
 */
export class FortuneCookieDataManager {
  constructor() {
    this.externalSave = new Phaser.Signal();
  }

  /**
   * Inits data with default values
   * @param {Object} dataReference
   */
  initData(dataReference) {
    this.dataReference = dataReference;

    const defaultData = {
      timesDismissed: 0, // Total number of times the cookie has been dismissed
      messagesSeen: [], // Messages seen array index
      timesOpened: 0, // Total number of times a cookie has been opened
      timesShared: 0, // Total number of times a cookie has been potentially shared
      timesFullDismiss: 0, // Total number of times the event was disabled from dismiss limit
      timesSpawned: 0, // Total number of times the cookie was spawned
      timesCollected: 0, // Total number of times the cookie was collected
    };

    this.mergeMissing(this.dataReference, defaultData);
  }

  /**
   * Merges missing data to the data reference
   * @param {Object} rtr
   * @param {Object} incoming
   */
  mergeMissing(rtr, incoming) {
    for (const key in incoming) {
      if (Object.prototype.hasOwnProperty.call(incoming, key) && !Object.prototype.hasOwnProperty.call(rtr, key)) rtr[key] = incoming[key];
    }
    return rtr;
  }

  /**
   * Whenever the user dismisses the fortune cookie, the counter goes up.
   * After it gets past the limit, it resets and enters the cooldown.
   */
  async triggerDismiss() {
    if (!(await OMT.feature.getFortuneCookieEvent())) {
      console.log('Fortune Cookie event not active');
      return;
    }
    this.dataReference.timesDismissed++;

    if ((this.dataReference.timesDismissed % G.featureUnlock.fortuneCookie.dismissLimit) === 0) {
      this.dataReference.timesFullDismiss++;
      this.setInactiveTillNextReset();
    }
  }

  /**
   * Here are your messages!
   * @returns {Array<{ i: number, c: string }>}
   */
  getMessages() {
    return this.dataReference.messagesSeen;
  }

  /**
   * Saves the message
   * @param {Object} messageData
   * @param {number} messageData.i The index of the message
   * @param {string} messageData.c The category of the message
   * @param {boolean} saveNow
   */
  setMessageSeen(messageData, saveNow = true) {
    this.dataReference.messagesSeen.push(messageData);
    if (saveNow) {
      this.externalSave.dispatch();
    }
  }

  /**
   * Removes all messages seen in the given category by simply remaking the array without it
   * @param {string} category
   */
  clearMessageCategory(category) {
    this.dataReference.messagesSeen = this.dataReference.messagesSeen.filter((message) => message.c !== category);
  }

  /**
   * Sets the fortune cookie to inactive until the next upcoming reset day
   */
  setInactiveTillNextReset() {
    const nextResetDay = new Date();
    const now = nextResetDay.getTime();
    nextResetDay.setUTCHours(0, 0, 0, 0);
    // Add on the number of days till the next reset day to today
    nextResetDay.setUTCDate(nextResetDay.getUTCDate() + ((7 - nextResetDay.getUTCDay()) % 7) + G.featureUnlock.fortuneCookie.resetDay);
    const difference = nextResetDay.getTime() - now;
    G.saveState.setUserCooldown(FORTUNE_COOLDOWN_KEY.COOLDOWN, '', difference, true);
  }

  /**
   * Gets number of times the fortune cookie window was dismissed
   * @returns {number}
   */
  get timesDismissed() {
    return this.dataReference.timesDismissed;
  }

  /**
   * Increments the number of times the fortune cookie window was opened
   */
  incrementOpened() {
    this.dataReference.timesOpened++;
  }

  /**
   * Returns the number of times the fortune cookie window was opened
   * @returns {number} 
   */
  get timesOpened() {
    return this.dataReference.timesOpened;
  }

  /**
   * Increments the number of times a message has been potentially shared
   */
  incrementShared() {
    this.dataReference.timesShared++;
  }

  /**
   * Returns the number of times a cookie has been shared
   * @returns {number}
   */
  get timesShared() {
    return this.dataReference.timesShared;
  }

  /**
   * Returns the number of times the fortune cookie event ended because of a full dismiss
   * @returns {number}
   */
  get timesFullDimiss() {
    return this.dataReference.timesFullDismiss;
  }

  /**
   * Increments the number of times the fortune cookie has been spawned
   */
  incrementSpawned() {
    this.dataReference.timesSpawned++;
  }

  /**
   * Returns the number of times the fortune cookie has been spawned
   * @returns {number}
   */
  get timesSpawned() {
    return this.dataReference.timesSpawned;
  }

  /**
   * Increments the number of times the fortune cookie has been collected
   * @returns {number}
   */
  incrementCollected() {
    this.dataReference.timesCollected++;
  }

  /**
   * Returns the number of times the fortune cookie has been collected
   * @returns {number}
   */
  get timesCollected() {
    return this.dataReference.timesCollected;
  }
}
